import { Box, Typography, Skeleton, Stack } from "@mui/joy";
import { type ReactNode } from "react";
import { Button } from "../../../../shared";
import { MaterialCard } from "./MaterialCard";
import {
    MaterialGroupBlock,
    MaterialSubgroupBlock,
} from "./MaterialGroupBlock";
import {
    materialGlassPanelSx,
    MATERIAL_ROW,
    materialRowDividerSx,
} from "./materialRowLayout";
import type {
    EditableMaterialCardProps,
    EditHandlers,
    FurnaceVentClusterProps,
    GroupRowsProps,
    MaterialRowProps,
    MaterialsProps,
} from "./types";
import {
    useMaterialEvaluator,
    useMaterialProductSelection,
    useMaterialAttributeDefaults,
    useEstimateContractContextManager,
} from "../../hooks";
import type {
    FurnaceVentMaterialRow,
    MaterialRowItem,
} from "../../utils/materialLineItems";
import { buildFurnaceVentTreeBySize } from "../../utils/materialLineItems";
import { EstimateSupplierComparison } from "..";

export default function Materials({
    projectId,
    materialsByGroup,
    loading: materialsLoading = false,
    error: materialsError = null,
}: MaterialsProps) {
    const {
        loading: evalLoading,
        error: evalError,
        formulaFlags,
    } = useMaterialEvaluator(materialsByGroup);
    const { values, patchMaterialEdit } = useEstimateContractContextManager();
    const { onProductChange } = useMaterialProductSelection(materialsByGroup);
    useMaterialAttributeDefaults(materialsByGroup);

    const loading = materialsLoading || evalLoading;
    const error = materialsError || evalError;

    const handlers: EditHandlers = {
        edits: values.materials,
        formulaFlags,
        onProductChange: (editKey, category, productId) => {
            onProductChange(editKey, category, productId);
        },
        onQuantityChange: (editKey, quantity) => {
            patchMaterialEdit(editKey, { quantity, quantityOverridden: true });
        },
        onResetToFormula: (editKey) => {
            const result = formulaFlags[editKey]?.result;
            if (result == null) return;
            patchMaterialEdit(editKey, {
                quantity: Number(result),
                quantityOverridden: false,
            });
        },
    };

    if (loading) {
        return (
            <Box sx={{ mt: 2 }}>
                <Stack spacing={2}>
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={56} />
                    ))}
                </Stack>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mt: 2, py: 3, textAlign: "center" }}>
                <Typography level="body-sm" color="danger">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 1 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                }}
            >
                <Box>
                    <Typography level="title-md">
                        Material & Component Selection
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                        Product specifications, manufacturer comparisons, and
                        inventory counts.
                    </Typography>
                </Box>
                <Button size="sm" variant="soft" sx={{ borderRadius: "4px" }}>
                    Add Material
                </Button>
            </Box>

            <Stack spacing={2.5}>
                {Object.entries(materialsByGroup).map(([groupName, rows]) => (
                    <Box
                        key={groupName}
                        sx={{
                            ...materialGlassPanelSx,
                            mx: { xs: -2, sm: -3 },
                        }}
                    >
                        <Box
                            sx={{
                                px: MATERIAL_ROW.px,
                                py: 1.25,
                                ...materialRowDividerSx,
                            }}
                        >
                            <Typography level="title-md">
                                {groupName}
                            </Typography>
                        </Box>

                        <GroupRows rows={rows} handlers={handlers} />
                    </Box>
                ))}

                {Object.keys(materialsByGroup).length === 0 && (
                    <Box
                        sx={{
                            ...materialGlassPanelSx,
                            py: 4,
                            px: 3,
                            textAlign: "center",
                            mx: { xs: -2, sm: -3 },
                        }}
                    >
                        <Typography level="body-sm" color="neutral">
                            No materials found for these specifications.
                        </Typography>
                    </Box>
                )}

                <EstimateSupplierComparison
                    projectId={projectId}
                    materialsByGroup={materialsByGroup}
                />
            </Stack>
        </Box>
    );
}

function GroupRows({ rows, handlers }: GroupRowsProps) {
    const nodes: ReactNode[] = [];
    let furnaceRows: FurnaceVentMaterialRow[] = [];

    const flushFurnace = () => {
        if (furnaceRows.length === 0) return;
        nodes.push(
            <FurnaceVentCluster
                key={`furnace-${nodes.length}`}
                rows={furnaceRows}
                handlers={handlers}
            />,
        );
        furnaceRows = [];
    };

    for (const row of rows) {
        if (isFurnaceVentRow(row)) {
            furnaceRows.push(row);
            continue;
        }

        flushFurnace();
        nodes.push(<MaterialRow key={row.id} row={row} handlers={handlers} />);
    }

    flushFurnace();
    return <>{nodes}</>;
}

function FurnaceVentCluster({ rows, handlers }: FurnaceVentClusterProps) {
    const bySize = buildFurnaceVentTreeBySize(rows);

    return (
        <MaterialGroupBlock
            title="Furnace Vent"
            subtitle="Jack, cap, and collar per size"
        >
            {bySize.map((branch) => (
                <MaterialSubgroupBlock key={branch.size} title={branch.size}>
                    {branch.lines.map((line) => (
                        <EditableMaterialCard
                            key={line.lineId}
                            editKey={line.lineId}
                            category={line.category}
                            item={{
                                productName: line.category,
                                lineLabel: line.category,
                                availableProducts: line.availableProducts,
                                selectedProductId: line.selectedProductId,
                                quantity: line.quantity,
                                priceRange: line.priceRange,
                                variant: line.type,
                                detail: line.flashingKit,
                                showCategoryInCard: false,
                                inGroup: true,
                            }}
                            handlers={handlers}
                        />
                    ))}
                </MaterialSubgroupBlock>
            ))}
        </MaterialGroupBlock>
    );
}

function MaterialRow({ row, handlers }: MaterialRowProps) {
    if (row.mode === "single") {
        return (
            <EditableMaterialCard
                editKey={row.id}
                category={row.category}
                item={row}
                handlers={handlers}
            />
        );
    }

    return (
        <MaterialGroupBlock title={row.category}>
            {row.lines.map((line) => (
                <EditableMaterialCard
                    key={line.lineId}
                    editKey={line.lineId}
                    category={row.category}
                    item={{
                        productName: row.category,
                        lineLabel: line.size,
                        availableProducts: line.availableProducts,
                        selectedProductId: line.selectedProductId,
                        quantity: line.quantity,
                        priceRange: line.priceRange,
                        showCategoryInCard: false,
                        inGroup: true,
                    }}
                    handlers={handlers}
                />
            ))}
        </MaterialGroupBlock>
    );
}

function EditableMaterialCard({
    editKey,
    category,
    item,
    handlers,
}: EditableMaterialCardProps) {
    const edit = handlers.edits[editKey];
    const selectedProductId = edit
        ? edit.selectedProductId
        : item.selectedProductId;
    const quantity = edit ? edit.quantity : item.quantity;
    const supplierUnitPrice =
        edit?.supplierUnitPrice ?? item.supplierUnitPrice ?? null;
    const supplierId = edit?.supplierId ?? item.supplierId ?? null;
    const supplierName = edit?.supplierName ?? item.supplierName ?? null;
    const formulaFlag = handlers.formulaFlags[editKey];

    return (
        <MaterialCard
            item={{
                ...item,
                selectedProductId,
                quantity,
                quantityOverridden: edit?.quantityOverridden,
                supplierUnitPrice,
                supplierId,
                supplierName,
                hasFormula: formulaFlag?.hasFormula,
                formulaActive: formulaFlag?.formulaActive,
                formula: formulaFlag,
            }}
            onProductChange={(productId) =>
                handlers.onProductChange(editKey, category, productId)
            }
            onQuantityChange={(quantity) =>
                handlers.onQuantityChange(editKey, quantity)
            }
            onResetToFormula={() => handlers.onResetToFormula(editKey)}
        />
    );
}

function isFurnaceVentRow(row: MaterialRowItem): row is FurnaceVentMaterialRow {
    return row.mode === "multi" && row.derivedFrom === "Furnace Vent";
}
